import { useEffect, useRef, useState } from "react";
import { cn } from "../../lib/utils";

export interface TitleProps {
  value: string;
  onUpdate: (newValue: string) => void;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
}

export function EditableText({
  value,
  onUpdate,
  className = "",
  disabled = false,
  placeholder,
}: TitleProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setEditValue(value);
  }, [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleClick = () => {
    if (!disabled) {
      setIsEditing(true);
    }
  };

  const handleBlur = () => {
    setIsEditing(false);
    if (editValue !== value) {
      const finalValue = editValue.trim() === "" ? value : editValue;
      onUpdate(finalValue);
      setEditValue(finalValue);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      setIsEditing(false);
      if (editValue !== value) {
        const finalValue = editValue.trim() === "" ? value : editValue;
        onUpdate(finalValue);
        setEditValue(finalValue);
      }
    } else if (e.key === "Escape") {
      setIsEditing(false);
      setEditValue(value);
    }
  };

  const commonClassNames = cn(
    "line-height-[2rem] focus:bg-accent-secondary-2 w-full",
    "outline-accent-secondary-1 border-none rounded bg-transparent",
    "bg-transparent",
    "transition-[outline] duration-200",
    {
      "hover:bg-accent-secondary-1 hover:outline-4": !disabled,
    },
    className,
  );

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyPress}
        className={cn(
          commonClassNames,
          "overflow-hidden",
          "outline-accent-secondary-2 outline-4",
          "field-sizing-content",
          className,
        )}
        onInput={(e) => {
          // Auto-resize based on content
          const target = e.target as HTMLTextAreaElement;
          target.style.height = "auto";
          target.style.height = `${target.scrollHeight}px`;
        }}
      />
    );
  }

  return (
    <h1
      onClick={handleClick}
      className={cn(
        commonClassNames,
        disabled ? "cursor-default" : "cursor-pointer",
        className,
        { "text-gray-500 italic": !value },
      )}
    >
      {value || placeholder || <>&nbsp;</>}
    </h1>
  );
}
